import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { useGetUnreadMessageQuery } from "../features/chat/chatsApiSlice";
import socket from "../config/socket";

// Create a context
const UnreadMessagesContext = createContext();

// Create a provider component
export const UnreadMessagesProvider = ({ children }) => {
  const {
    data: unreadMessagesData,
    isSuccess: isSuccessUnreadMessages,
    isError: isErrorUnreadMessages,
    error: unreadMessagesError,
    refetch: refetchUnreadMessages
  } = useGetUnreadMessageQuery();

  const [onlineUsers, setOnlineUsers] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState([]);
  const errorShown = useRef(false);

  // Set up socket event listeners
  useEffect(() => {
    const receiveMessageHandler = (data) => {
      //console.log("New message received:", data);
      // Add this new message to our unread messages list
      // Make sure not to add duplicates
      setUnreadMessages((prevMessages) => {
        // Check if this message is already in the list
        const isExisting = prevMessages.some(
          (msg) => msg.message._id === data.message._id
        );
        
        if (isExisting) {
          return prevMessages;
        }
        
        return [...prevMessages, data];
      });
    };

    socket.on("recieve_new_message", receiveMessageHandler);

    socket.on("getUsers", (users) => {
      //console.log("online users", users)
      setOnlineUsers(users);
    });

    // Cleanup
    return () => {
      socket.off("recieve_new_message", receiveMessageHandler);
      socket.off("getUsers");
    };
  }, []);

  // Initial load of unread messages from API
  useEffect(() => {
    if (
      isSuccessUnreadMessages &&
      !isErrorUnreadMessages &&
      unreadMessagesData?.unreadMessages?.length > 0
    ) {
      setUnreadMessages(unreadMessagesData?.unreadMessages);
    }
    
    if (isErrorUnreadMessages && !errorShown.current) {
      console.error("Error fetching unread messages:", unreadMessagesError);
      errorShown.current = true;
      
      // If the error is a 403, we might need to refresh the token
      if (unreadMessagesError?.status === 403 || unreadMessagesError?.status === 401) {
        //console.log("Authentication issue with unread messages, will retry soon");
        // Try to refetch after a short delay
        const timer = setTimeout(() => {
          refetchUnreadMessages();
          errorShown.current = false;
        }, 5000);
        return () => clearTimeout(timer);
      }
    }
  }, [isSuccessUnreadMessages, isErrorUnreadMessages, unreadMessagesData, unreadMessagesError, refetchUnreadMessages]);

  // Function to mark a message as read and remove it from unread messages
  const markMessageAsRead = (chatId, messageId) => {
    setUnreadMessages((prevMessages) =>
      prevMessages.filter(
        (message) =>
          message.chatId !== chatId || message.message._id !== messageId
      )
    );
  };

  const markAllMessagesOfChatAsRead = (chatId) => {
    setUnreadMessages((prevMessages) =>
      prevMessages.filter((message) => message.chatId !== chatId)
    );
  };

  const unReadMessagesOfChat = (chatId) => {
    return unreadMessages.filter((message) => message.chatId === chatId);
  };

  //console.log("unReadMessages in Context", unreadMessages);

  return (
    <UnreadMessagesContext.Provider
      value={{
        unreadMessages,
        markMessageAsRead,
        unReadMessagesOfChat,
        markAllMessagesOfChatAsRead,
        onlineUsers,
        refetchUnreadMessages
      }}
    >
      {children}
    </UnreadMessagesContext.Provider>
  );
};

// Create a custom hook to access the context
export const useUnreadMessages = () => {
  const context = useContext(UnreadMessagesContext);
  if (!context) {
    throw new Error(
      "useUnreadMessages must be used within a UnreadMessagesProvider"
    );
  }
  return context;
};